import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface NaturalLanguageQueryRequest {
    query: string;
    companyNo: string;
    model?: string;
    maxRows?: number;
}

export interface ColumnDefinition {
    name: string;
    displayName: string;
    dataType: string;
    format?: string;
    width?: number;
    sortable: boolean;
    filterable: boolean;
}

export interface DefaultSort {
    columnName: string;
    direction: 'asc' | 'desc';
}

export interface HighlightRule {
    columnName: string;
    condition: string;
    value: any;
    color: string;
}

export interface PresentationHints {
    title?: string;
    description?: string;
    defaultSort?: DefaultSort;
    highlightRules?: HighlightRule[];
}

export interface NaturalLanguageQueryResponse {
    success: boolean;
    queryId: string;
    generatedSQL?: string;
    explanation?: string;
    executionTimeMs: number;
    rowCount: number;
    tokensUsed?: number;
    timestamp: string;
    columns: ColumnDefinition[];
    rows: Record<string, any>[];
    presentation?: PresentationHints;
    errorMessage?: string;
    errorType?: string;
}

// Data Processing Types
export interface SelectedDataset {
    queryId: string;
    originalQuery: string;
    rows: Record<string, any>[];
}

export interface DataProcessingRequest {
    query: string;
    companyNo: string;
    selectedDatasets: SelectedDataset[];
    model?: string;
}

export interface TextOutput {
    content: string;
    format: string;
}

export interface ChartSeries {
    name: string;
    data: Record<string, any>[];
    color?: string;
    dataKey?: string;
}

export interface ChartAxisConfig {
    label?: string;
    dataKey: string;
    format?: string;
}

export interface ChartOutput {
    chartType: string;
    title: string;
    series: ChartSeries[];
    xAxis?: ChartAxisConfig;
    yAxis?: ChartAxisConfig;
}

export interface TableOutput {
    title?: string;
    description?: string;
    columns: ColumnDefinition[];
    rows: Record<string, any>[];
}

export interface BinaryOutput {
    mimeType: string;
    base64Data: string;
    filename: string;
}

export interface DataProcessingResponse {
    success: boolean;
    outputType: 'text' | 'chart' | 'pdf' | 'image' | 'table';
    data: TextOutput | ChartOutput | TableOutput | BinaryOutput | any;
    explanation?: string;
    executionTimeMs: number;
    tokensUsed?: number;
    timestamp: string;
    errorMessage?: string;
    errorType?: string;
}

// Chat Types (Spør Compass)
export interface ChatRequestDTO {
    message: string;
    conversationHistory: ChatHistoryMessageDTO[];
    maxRows?: number;
}

export interface ChatHistoryMessageDTO {
    role: string;
    content: string;
    hasData: boolean;
    dataSummary?: string;
    generatedSQL?: string;
}

export interface ChatResponseDTO {
    success: boolean;
    message: string;
    blocks: ResponseBlockDTO[];
    generatedSQL?: string;
    executionTimeMs: number;
    tokensUsed?: number;
    cacheHit: boolean;
    conversationTitle?: string;
    suggestedFollowups: string[];
    errorMessage?: string;
    errorType?: string;
}

export interface ResponseBlockDTO {
    type: string;
    content?: string;
    tableData?: any;
    chartData?: any;
    kpiData?: any;
    fileData?: any;
    funnelData?: any;
    heatmapData?: any;
    treemapData?: any;
}

// Export types
export interface ExportRequestDTO {
    format: 'csv' | 'xlsx' | 'json' | 'html';
    title: string;
    columns: ExportColumnDTO[];
    rows: Record<string, any>[];
    generatedSQL?: string;
}

export interface ExportColumnDTO {
    name: string;
    displayName: string;
    dataType: string;
    format?: string;
}

export interface ExportResponseDTO {
    fileId: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    downloadUrl: string;
}

// ============================================================================
// SavedReport Types
// ============================================================================

export interface SavedReportDTO {
    reportId: number;
    name: string;
    description?: string;
    generatedSQL: string;
    originalPrompt?: string;
    parameters?: string;
    exportFormat: string;
    chartConfig?: string;
    category?: string;
    iconName?: string;
    sortOrder: number;
    isFavorite: boolean;
    scheduleEnabled: boolean;
    scheduleCron?: string;
    scheduleFormat?: string;
    lastRunAt?: string;
    lastRunStatus?: string;
    lastRunRowCount?: number;
    nextRunAt?: string;
    deliveryFormat?: string;
    deliveryMethod?: string;
    deliveryEmail?: string;
    formatPrompt?: string;
    useClaude: boolean;
    createdBy: string;
    createdAt: string;
    updatedBy?: string;
    updatedAt?: string;
}

export interface CreateReportDTO {
    name: string;
    description?: string;
    generatedSQL: string;
    originalPrompt?: string;
    parameters?: string;
    exportFormat?: string;
    chartConfig?: string;
    category?: string;
    iconName?: string;
    sortOrder?: number;
    isFavorite?: boolean;
    scheduleEnabled?: boolean;
    scheduleCron?: string;
    scheduleFormat?: string;
    deliveryFormat?: string;
    deliveryMethod?: string;
    deliveryEmail?: string;
    formatPrompt?: string;
    useClaude?: boolean;
}

export interface UpdateReportDTO {
    name?: string;
    description?: string;
    generatedSQL?: string;
    originalPrompt?: string;
    parameters?: string;
    exportFormat?: string;
    chartConfig?: string;
    category?: string;
    iconName?: string;
    sortOrder?: number;
    isFavorite?: boolean;
    scheduleEnabled?: boolean;
    scheduleCron?: string;
    scheduleFormat?: string;
    deliveryFormat?: string;
    deliveryMethod?: string;
    deliveryEmail?: string;
    formatPrompt?: string;
    useClaude?: boolean;
}

export interface ReportExecutionDTO {
    executionId: number;
    reportId: number;
    startedAt: string;
    completedAt?: string;
    status: string;
    rowCount?: number;
    executionTimeMs?: number;
    triggerType: string;
    triggeredBy?: string;
    resultSnapshot?: string;
    errorMessage?: string;
}

export interface ConversationListDTO {
    conversationId: number;
    title?: string;
    createdAt: string;
    lastMessageAt?: string;
    messageCount: number;
    isArchived: boolean;
}

export interface ConversationDetailDTO {
    conversationId: number;
    title?: string;
    messages?: string;
    createdAt: string;
    lastMessageAt?: string;
    messageCount: number;
    isArchived: boolean;
}

export interface SaveConversationDTO {
    conversationId?: number;
    title?: string;
    messages?: string;
    messageCount: number;
}

// ============================================================================
// Digest Types (Daglig Sammendrag)
// ============================================================================

export interface DigestSectionDTO {
    sectionType: string;
    sourceId: string;
    title?: string;
    sortOrder: number;
    maxRows?: number;
    /** Om et diagram skal inkluderes i digest-eposten for denne seksjonen */
    includeChart?: boolean;
}

export interface DigestDTO {
    digestId: number;
    name: string;
    description?: string;
    sections: DigestSectionDTO[];
    scheduleEnabled: boolean;
    scheduleCron?: string;
    lastRunAt?: string;
    lastRunStatus?: string;
    nextRunAt?: string;
    deliveryEmail: string;
    headerTitle: string;
    headerSubtitle?: string;
    createdBy: string;
    createdAt: string;
    updatedBy?: string;
    updatedAt?: string;
}

export interface CreateDigestDTO {
    name: string;
    description?: string;
    sections: DigestSectionDTO[];
    scheduleEnabled: boolean;
    scheduleCron?: string;
    deliveryEmail: string;
    headerTitle?: string;
    headerSubtitle?: string;
}

export interface UpdateDigestDTO {
    name?: string;
    description?: string;
    sections?: DigestSectionDTO[];
    scheduleEnabled?: boolean;
    scheduleCron?: string;
    deliveryEmail?: string;
    headerTitle?: string;
    headerSubtitle?: string;
}

export interface DigestExecuteResultDTO {
    digestId: number;
    success: boolean;
    executedAt: string;
    message?: string;
}

export interface AvailableMetricDTO {
    key: string;
    name: string;
    description: string;
}

// ============================================================================
// Quick Widget Types (NL til Dashboard)
// ============================================================================

export interface QuickWidgetResponseDTO {
    success: boolean;
    reportId?: number;
    reportName?: string;
    displayMode?: string;
    errorMessage?: string;
}

// ============================================================================
// Widget Types (Dashboard-integrasjon)
// ============================================================================

export interface WidgetReportDataDTO {
    reportId: number;
    reportName: string;
    success: boolean;
    rowCount: number;
    executionTimeMs: number;
    executedAt: string;
    cacheHit: boolean;
    resultSnapshot?: string;
    errorMessage?: string;
    exportFormat: string;
    chartConfig?: string;
}

class ClaudeQueryService {
    async sendMessage(request: ChatRequestDTO): Promise<ChatResponseDTO> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/chat`,
            request
        );

        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Chat request failed'));
        }

        return await response.json();
    }

    async executeQuery(request: NaturalLanguageQueryRequest): Promise<NaturalLanguageQueryResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/execute`,
            request
        );

        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Failed to execute query'));
        }

        return await response.json();
    }

    async processData(request: DataProcessingRequest): Promise<DataProcessingResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/process-data`,
            request
        );

        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Failed to process data'));
        }

        return await response.json();
    }

    async getSchema(companyNo: string): Promise<string> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/schema/${companyNo}`
        );

        if (!response.ok) {
            throw new Error(`Failed to get schema: ${response.status}`);
        }

        return await response.text();
    }

    async getCurrentSchema(): Promise<string> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/schema`
        );

        if (!response.ok) {
            throw new Error(`Failed to get schema: ${response.status}`);
        }

        return await response.text();
    }

    /**
     * Eksporter tabelldata til fil (CSV, Excel, JSON, HTML).
     * Returnerer metadata med downloadUrl. Kaller deretter downloadExportFile for selve filen.
     */
    async exportData(request: ExportRequestDTO): Promise<ExportResponseDTO> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/export`,
            request
        );

        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Export failed'));
        }

        return await response.json();
    }

    /**
     * Last ned en eksportert fil direkte som Blob (for bruker-nedlasting)
     */
    async downloadExportFile(fileId: string): Promise<{ blob: Blob; filename: string }> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/export/${fileId}`
        );

        if (!response.ok) {
            throw new Error(`Download failed: ${response.status}`);
        }

        const blob = await response.blob();
        const contentDisposition = response.headers.get('content-disposition');
        const filenameMatch = contentDisposition?.match(/filename[^;=\n]*=(['"]?)([^;\n]*)\1/);
        const filename = filenameMatch?.[2] || `export-${fileId}`;

        return { blob, filename };
    }

    // ========================================================================
    // Quick Widget (NL til Dashboard)
    // ========================================================================

    async createQuickWidget(question: string): Promise<QuickWidgetResponseDTO> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/quick-widget`,
            { question }
        );

        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Quick widget creation failed'));
        }

        return await response.json();
    }

    // ========================================================================
    // SavedReport CRUD
    // ========================================================================

    async getReports(): Promise<SavedReportDTO[]> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/reports`
        );
        if (!response.ok) throw new Error(`Failed to get reports: ${response.status}`);
        return await response.json();
    }

    async getReport(id: number): Promise<SavedReportDTO> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/reports/${id}`
        );
        if (!response.ok) throw new Error(`Failed to get report: ${response.status}`);
        return await response.json();
    }

    async createReport(dto: CreateReportDTO): Promise<SavedReportDTO> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/reports`,
            dto
        );
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Failed to create report'));
        }
        return await response.json();
    }

    async updateReport(id: number, dto: UpdateReportDTO): Promise<void> {
        const response = await AuthService.authenticatedPut(
            `${API_BASE_URL}/claude-query/reports/${id}`,
            dto
        );
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Failed to update report'));
        }
    }

    async deleteReport(id: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(
            `${API_BASE_URL}/claude-query/reports/${id}`
        );
        if (!response.ok) throw new Error(`Failed to delete report: ${response.status}`);
    }

    async executeReport(id: number): Promise<ReportExecutionDTO> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/reports/${id}/execute`,
            {}
        );
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Failed to execute report'));
        }
        return await response.json();
    }

    async getReportHistory(id: number): Promise<ReportExecutionDTO[]> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/reports/${id}/history`
        );
        if (!response.ok) throw new Error(`Failed to get report history: ${response.status}`);
        return await response.json();
    }

    // ========================================================================
    // Conversations
    // ========================================================================

    async getConversations(): Promise<ConversationListDTO[]> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/conversations`
        );
        if (!response.ok) throw new Error(`Failed to get conversations: ${response.status}`);
        return await response.json();
    }

    async getConversation(id: number): Promise<ConversationDetailDTO> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/conversations/${id}`
        );
        if (!response.ok) throw new Error(`Failed to get conversation: ${response.status}`);
        return await response.json();
    }

    async saveConversation(dto: SaveConversationDTO): Promise<ConversationDetailDTO> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/conversations`,
            dto
        );
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Failed to save conversation'));
        }
        return await response.json();
    }

    async deleteConversation(id: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(
            `${API_BASE_URL}/claude-query/conversations/${id}`
        );
        if (!response.ok) throw new Error(`Failed to delete conversation: ${response.status}`);
    }

    // ========================================================================
    // Digest CRUD (Daglig Sammendrag)
    // ========================================================================

    async getDigests(): Promise<DigestDTO[]> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/digests`
        );
        if (!response.ok) throw new Error(`Failed to get digests: ${response.status}`);
        return await response.json();
    }

    async getDigest(id: number): Promise<DigestDTO> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/digests/${id}`
        );
        if (!response.ok) throw new Error(`Failed to get digest: ${response.status}`);
        return await response.json();
    }

    async createDigest(dto: CreateDigestDTO): Promise<DigestDTO> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/digests`,
            dto
        );
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Failed to create digest'));
        }
        return await response.json();
    }

    async updateDigest(id: number, dto: UpdateDigestDTO): Promise<void> {
        const response = await AuthService.authenticatedPut(
            `${API_BASE_URL}/claude-query/digests/${id}`,
            dto
        );
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Failed to update digest'));
        }
    }

    async deleteDigest(id: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(
            `${API_BASE_URL}/claude-query/digests/${id}`
        );
        if (!response.ok) throw new Error(`Failed to delete digest: ${response.status}`);
    }

    async executeDigest(id: number): Promise<DigestExecuteResultDTO> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/digests/${id}/execute`,
            {}
        );
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Failed to execute digest'));
        }
        return await response.json();
    }

    async getAvailableMetrics(): Promise<AvailableMetricDTO[]> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/digests/available-metrics`
        );
        if (!response.ok) throw new Error(`Failed to get available metrics: ${response.status}`);
        return await response.json();
    }

    // ========================================================================
    // Widget Data (Dashboard-integrasjon)
    // ========================================================================

    async getWidgetData(reportId: number): Promise<WidgetReportDataDTO> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/reports/${reportId}/widget-data`
        );
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'Failed to get widget data'));
        }
        return await response.json();
    }
}

export const claudeQueryService = new ClaudeQueryService();
